import Button from '../Button.js';
import Contact from '../Contact.js';
import { allowsAssistant } from '../engine/escalation.js';
import Icon from '../Icon.js';
import OptionRow from './OptionRow.js';
import Services from '../Services.js';
import StepFrame from './StepFrame.js';
import Theme from '../styles/Theme.js';
import Utils from '../Utils.js';

/*
 * EscalateStep
 *
 * The handover, and the last screen before we start asking for details.
 *
 * IT ASKS HOW TO REACH US RATHER THAN JUST ANNOUNCING ITSELF. Call and write
 * used to be a screen of their own, immediately after this one, which meant two
 * pages in a row that collected nothing: one saying a person will pick this up,
 * and one asking how. They are the same beat. A screen whose entire content is
 * "click Continue" earns its place only if something on it needed reading, and
 * this one now carries a decision.
 *
 * Contact details are still collected at the END. This is the screen that
 * explains why they are suddenly being asked, which is the point of the
 * correction that moved them here: nobody should hand over a name and an
 * address to read an article.
 *
 * WHO it reaches is not named. For OmniShield that depends on a lookup that has
 * not run yet, and promising a dealership before we know there is one would be a
 * promise made by the wrong screen.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const EscalateStep = ({
  brand,
  node,
  onEscalate,
  stepKey
}) => {
  const mobile = Utils.isMobile();
  const canCall = Contact.forBrand(brand) !== null;

  // On a dealership's own website the person at the other end is not a mystery.
  const owner = Services.dealership();
  return /*#__PURE__*/_jsxs(StepFrame
  /* With no number to give there is no decision to make, so it goes back
     to being a single way onward rather than a menu of one. */, {
    actions: canCall ? null : /*#__PURE__*/_jsx(Button, {
      icon: 'arrow-right',
      label: node.action || 'Continue',
      onClick: onEscalate.bind(this, node.reason, null)
    }),
    eyebrow: 'Next step',
    footnote: owner?.id ? `We only ask for what ${owner.name} needs to pick this up, and nothing else.` : 'We only ask for what a person needs to pick this up, and nothing else.',
    stepKey: stepKey,
    title: node.title || 'Let us get a person on this',
    children: [/*#__PURE__*/_jsxs("div", {
      style: {
        alignItems: 'flex-start',
        backgroundColor: Theme.tint(0.05),
        border: `1px solid ${Theme.tint(0.2)}`,
        borderRadius: 14,
        display: 'flex',
        gap: 14,
        marginBottom: canCall ? mobile ? 26 : 32 : 0,
        padding: mobile ? '18px 18px' : '20px 22px'
      },
      children: [/*#__PURE__*/_jsx(Icon, {
        color: Theme.colors.primary(),
        name: 'people-fill',
        size: 18,
        style: {
          flexShrink: 0,
          marginTop: 2
        }
      }), /*#__PURE__*/_jsx("span", {
        style: Theme.textStyles.body({
          color: Theme.colors.text(),
          fontSize: mobile ? 15 : 16,
          lineHeight: 1.65
        }),
        children: node.summary || (owner?.id ? `This one needs a set of eyes on it. We will ask for a few details, send ${owner.name} everything you have already told us, and they will follow up.` : 'This one needs a set of eyes on it. We will ask for a few details, send over everything you have already told us, and someone will follow up.')
      })]
    }), canCall && /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx("span", {
        style: {
          ...Theme.textStyles.subHeading({
            fontSize: mobile ? 16 : 18
          }),
          display: 'block',
          marginBottom: 14
        },
        children: 'How would you like to reach us?'
      }), /*#__PURE__*/_jsxs("div", {
        style: {
          display: 'flex',
          flexDirection: 'column',
          gap: 10
        },
        children: [brand?.assistant === true && allowsAssistant(node.reason) && /*#__PURE__*/_jsx(OptionRow, {
          help: 'Our AI assistant. Fastest if you want to keep troubleshooting.',
          icon: 'stars',
          label: 'I would rather chat with GRACI AI',
          onClick: onEscalate.bind(this, node.reason, 'chat')
        }), /*#__PURE__*/_jsx(OptionRow, {
          help: 'Everything you have told us goes with it, so nothing gets repeated.',
          icon: 'envelope-fill',
          label: 'I would rather send it in writing',
          onClick: onEscalate.bind(this, node.reason, 'write')
        }), /*#__PURE__*/_jsx(OptionRow, {
          help: 'We will give you the number to speak with a support representative.',
          icon: 'telephone-fill',
          label: 'I would rather call',
          onClick: onEscalate.bind(this, node.reason, 'call')
        })]
      })]
    })]
  });
};
export default EscalateStep;